import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import {
  Bell,
  MapPin,
  Crown,
  Globe,
  Clock,
  ChartLineUp,
  ArrowRight,
} from "@phosphor-icons/react";
import LanguageMenu, { LANGUAGES } from "@/components/LanguageMenu";
import { toast } from "sonner";

function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
}

export default function Settings() {
  const { user, refresh } = useAuth();
  const [billing, setBilling] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: b }, { data: s }] = await Promise.all([
          api.get("/billing/me"),
          api.get("/me/cron-summary"),
        ]);
        setBilling(b);
        setSummary(s);
      } catch {
        toast.error("Couldn't load settings");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const langName = LANGUAGES.find((l) => l.code === (user?.language || "en"))?.name || "English";

  return (
    <div className="min-h-screen bg-sand-50" data-testid="settings-page">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 lg:px-10 py-10 space-y-6">
        <div>
          <div className="font-mono-data text-mute">your account</div>
          <h1 className="font-display text-4xl md:text-5xl text-ink leading-tight">Settings</h1>
        </div>

        <div className="bg-sand-100 border border-sand-200 rounded-2xl p-6">
          <div className="font-mono-data text-mute mb-3">profile</div>
          <div className="grid sm:grid-cols-2 gap-4 text-ink">
            <Row label="Email" value={user?.email || "—"} />
            <Row label="Name" value={user?.name || "—"} />
            <Row label="Role" value={user?.role || "user"} />
            <Row label="Diagnosis language" value={
              <div className="flex items-center gap-2">
                <span className="font-display text-xl">{langName}</span>
                <LanguageMenu compact />
              </div>
            } />
          </div>
        </div>

        <div className="bg-sand-100 border border-sand-200 rounded-2xl p-6" data-testid="settings-subscription">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="font-mono-data text-mute mb-2">subscription</div>
              <div className="flex items-center gap-2">
                <Crown size={22} weight="duotone" className={billing?.is_pro ? "text-ochre" : "text-mute"} />
                <span className="font-display text-3xl text-ink">
                  {billing?.is_pro ? "Grower" : "Field"}
                </span>
              </div>
              {billing?.is_pro ? (
                <p className="text-mute mt-1">Active until <span className="font-mono-data text-ink">{fmtDate(billing.pro_until)}</span>.</p>
              ) : (
                <p className="text-mute mt-1">You're on the free Field plan.</p>
              )}
            </div>
            <Link to="/pricing" className="btn-primary !text-sm" data-testid="settings-manage-subscription">
              {billing?.is_pro ? "Extend plan" : "Upgrade to Grower"} <ArrowRight size={14} />
            </Link>
          </div>
        </div>

        <div className="bg-sand-100 border border-sand-200 rounded-2xl p-6" data-testid="settings-monitor">
          <div className="font-mono-data text-mute mb-3">climate monitor</div>
          {loading ? (
            <div className="font-mono-data text-mute animate-pulse-soft">loading…</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-forest text-white flex items-center justify-center"><MapPin size={18} weight="duotone" /></div>
                <div>
                  <div className="font-mono-data text-mute">monitored location</div>
                  <div className="font-display text-xl text-ink">{summary?.monitored_location?.label || "Not set yet"}</div>
                  <div className="text-mute text-xs mt-1">
                    {summary?.monitored_location?.latitude != null
                      ? `${summary.monitored_location.latitude.toFixed(3)}, ${summary.monitored_location.longitude.toFixed(3)}`
                      : "Tap \"Check climate now\" on the Plants page to set your monitoring location."}
                  </div>
                  <div className="text-mute text-xs mt-1">Last manual check: {fmtDate(summary?.monitored_location?.last_checked_at)}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-ochre text-ink flex items-center justify-center"><Clock size={18} weight="duotone" /></div>
                <div>
                  <div className="font-mono-data text-mute">last daily sweep</div>
                  <div className="font-display text-xl text-ink">{fmtDate(summary?.last_cron_run?.ran_at)}</div>
                  <div className="text-mute text-xs mt-1">
                    {summary?.last_cron_run
                      ? `${summary.last_cron_run.users_checked} users checked · ${summary.last_cron_run.alerts_created} alerts fired`
                      : "Cron hasn't run yet."}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-terra text-white flex items-center justify-center"><Bell size={18} weight="duotone" /></div>
                <div>
                  <div className="font-mono-data text-mute">your alerts (24h)</div>
                  <div className="font-display text-2xl text-ink">{summary?.alerts_last_24h ?? 0}</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-forest text-white flex items-center justify-center"><ChartLineUp size={18} weight="duotone" /></div>
                <div>
                  <div className="font-mono-data text-mute">your alerts (7 days)</div>
                  <div className="font-display text-2xl text-ink">{summary?.alerts_last_7d ?? 0}</div>
                </div>
              </div>
            </div>
          )}
          <Link to="/notifications" className="btn-secondary !text-sm mt-5 inline-flex"><Bell size={14} /> View notifications</Link>
        </div>

        {user?.role === "admin" && (
          <Link to="/admin" className="block bg-ink text-white rounded-2xl p-6 hover:bg-forest transition" data-testid="settings-admin-link">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-mono-data text-white/60">admin</div>
                <div className="font-display text-2xl">Open admin dashboard</div>
                <div className="text-white/70 text-sm mt-1">Cron runs, user roster, subscription health.</div>
              </div>
              <ArrowRight size={20} weight="bold" />
            </div>
          </Link>
        )}
      </main>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="font-mono-data text-mute">{label}</div>
      <div className="text-ink mt-1">{value}</div>
    </div>
  );
}
